import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './smartConditionSummary.scss';
import { IconDiagnosis, IconPrediction, IconRecommendation } from 'svgIcons/MotionPortalIcons';
import { getTranslateSmartConditionTexts, getTranslateSmartConditionTitle, parseRecommendationText } from '../helpers';
import { GaugeDefaults } from './RulGaugeChart/constants';
import UseChartConfig from './RulGaugeChart/useChartConfig';

const DiagnosisCard = ({ diagnosis, diagnosisNumber = 1 }) => {
    const showThresoldInfo = false;
    const { t: translate } = useTranslation();

    if (!diagnosis || !diagnosis?.failureType) {
        return null;
    }

    const renderRecommendationText = (recommendation) => {
        const text = getTranslateSmartConditionTexts(recommendation);

        if (!text) {
            return null;
        }

        const { isFormatted, prefix, items, suffix } = parseRecommendationText(text);

        if (isFormatted && items?.length > 0) {
            return (
                <>
                    {prefix &&
                        <p className='diagnosis-card__text' id='diagnosis-card__recommendation-prefix'>
                            {prefix}
                        </p>
                    }
                    <ol className='diagnosis-card__recommendation-list' id='diagnosis-card__recommendation-list'>
                        {items.map((item, index) =>
                            <li key={index} className='diagnosis-card__recommendation-item'>
                                {item}
                            </li>
                        )}
                    </ol>
                    {suffix &&
                        <p className='diagnosis-card__text' id='diagnosis-card__recommendation-suffix'>
                            {suffix}
                        </p>
                    }
                </>
            );
        }

        return (
            <p className='diagnosis-card__text'>
                {suffix || text}
            </p>
        );
    };

    const renderBadge = (faultLevel) => {
        const levelText = getTranslateSmartConditionTexts(faultLevel);
        let badgeClass = 'diagnosis-card__badge';

        if (levelText?.toLowerCase().includes('strongly')) {
            badgeClass += ' diagnosis-card__badge--strong';
        } else if (levelText?.toLowerCase().includes('weakly')) {
            badgeClass += ' diagnosis-card__badge--weak';
        }

        return <span className={badgeClass}>{levelText}</span>;
    };

    const renderPredictionWithRUL = () => {
        return (
            <>
                <div className='diagnosis-card__prediction-content-grid' id='diagnosis-card__prediction-content-grid'>
                    <div className='diagnosis-card__prediction-col' id='diagnosis-card__prediction-col-rul'>
                        <div className='diagnosis-card__prediction-subtitle' id='diagnosis-card__prediction-subtitle-rul'>
                            {translate('ABB.Powertrain.Frontend.RemainingUsefulLifeLabel')}
                        </div>
                        <div className='diagnosis-card__rul-gauge' id='diagnosis-card__rul-gauge'>
                            <UseChartConfig
                                value={diagnosis?.remainingUsefulLifeTime}
                                min={GaugeDefaults.MIN}
                                max={GaugeDefaults.MAX}
                                unit={GaugeDefaults.UNIT}
                            />
                        </div>
                    </div>
                    <div className='diagnosis-card__prediction-col' id='diagnosis-card__prediction-col-prediction'>
                        <div className='diagnosis-card__prediction-subtitle' id='diagnosis-card__prediction-subtitle-prediction'>
                            {getTranslateSmartConditionTitle(diagnosis?.prediction)}
                        </div>
                        {showThresoldInfo &&
                            <div className='diagnosis-card__days-list' id='diagnosis-card__days-list'>
                                <div className='diagnosis-card__days__Alert-item' id='diagnosis-card__days__Alert-item'>
                                    <div className='diagnosis-card__days__Alert-value' id='diagnosis-card__days__Alert-value'>{diagnosis?.alertThreshold}</div>
                                    <div className='diagnosis-card__days__Alert-label' id='diagnosis-card__days__Alert-label'>
                                        {translate('ABB.Powertrain.Frontend.daysToAlertLabel')}
                                    </div>
                                </div>
                                <div className='diagnosis-card__days__Alarm-item' id='diagnosis-card__days__Alarm-item'>
                                    <div className='diagnosis-card__days__Alarm-value' id='diagnosis-card__days__Alarm-value'>{diagnosis?.alarmThreshold}</div>
                                    <div className='diagnosis-card__days__Alarm-label' id='diagnosis-card__days__Alarm-label'>
                                        {translate('ABB.Powertrain.Frontend.daysToAlarmLabel')}
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <p className='diagnosis-card__text' id='diagnosis-card__prediction-text'>
                    {getTranslateSmartConditionTexts(diagnosis?.prediction)}
                </p>
            </>
        );
    };

    const renderPredictionSimple = () => {
        return (
            <>
                <div className='diagnosis-card__prediction-subtitle' id='diagnosis-card__prediction-subtitle-simple'>
                    {getTranslateSmartConditionTitle(diagnosis?.prediction)}
                </div>

                {showThresoldInfo && <div className='diagnosis-card__days-list diagnosis-card__days-list--simple' id='diagnosis-card__days-list-simple'>
                    <div className='diagnosis-card__days__Alert-item' id='diagnosis-card__days__Alert-item-simple'>
                        <div className='diagnosis-card__days__Alert-value' id='diagnosis-card__days__Alert-value-simple'>{diagnosis?.alertThreshold}</div>
                        <div className='diagnosis-card__days__Alert-label' id='diagnosis-card__days__Alert-label-simple'>
                            {translate('ABB.Powertrain.Frontend.daysToAlertLabel')}
                        </div>
                    </div>
                    <div className='diagnosis-card__days__Alarm-item' id='diagnosis-card__days__Alarm-item-simple'>
                        <div className='diagnosis-card__days__Alarm-value' id='diagnosis-card__days__Alarm-value-simple'>{diagnosis?.alarmThreshold}</div>
                        <div className='diagnosis-card__days__Alarm-label' id='diagnosis-card__days__Alarm-label-simple'>
                            {translate('ABB.Powertrain.Frontend.daysToAlarmLabel')}
                        </div>
                    </div>
                </div>
                }
                <p className='diagnosis-card__text' id='diagnosis-card__prediction-text-simple'>
                    {getTranslateSmartConditionTexts(diagnosis?.prediction)}
                </p>
            </>
        );
    };

    return (
        <div className='diagnosis-card' id='diagnosis-card'>
            <div className='diagnosis-card__grid' id='diagnosis-card__grid'>
                {/* Diagnosis Card */}
                <div className='diagnosis-card__card diagnosis-card__diagnosis-card' id='diagnosis-card__diagnosis-card'>
                    <div className='diagnosis-card__section-header' id='diagnosis-card__section-header'>
                        <span className='diagnosis-card__section-icon' id='diagnosis-card__section-icon'><IconDiagnosis /></span>
                        <h3 id='diagnosis-card__section-title'>{translate('ABB.Powertrain.Frontend.diagnosisLabel')} {diagnosisNumber}</h3>
                    </div>

                    <div className='diagnosis-card__failure-type' id='diagnosis-card__failure-type'>
                        {getTranslateSmartConditionTexts(diagnosis?.failureType)}
                    </div>

                    <p className='diagnosis-card__text' id='diagnosis-card__diagnosis-text'>
                        {getTranslateSmartConditionTexts(diagnosis?.diagnosis)}
                    </p>

                    {diagnosis?.faultDetectionLevel && renderBadge(diagnosis?.faultDetectionLevel)}

                    {/* Potential Causes Section */}
                    {diagnosis?.causes && diagnosis?.causes?.text &&
                        <>
                            <h4 className='diagnosis-card__causes-title' id='diagnosis-card__causes-title'>
                                {getTranslateSmartConditionTitle(diagnosis?.causes)}
                            </h4>
                            <p className='diagnosis-card__text' id='diagnosis-card__causes-text'>
                                {getTranslateSmartConditionTexts(diagnosis?.causes)}
                            </p>
                        </>
                    }
                </div>

                {/* Prediction Card */}
                <div className='diagnosis-card__card diagnosis-card__prediction-card' id='diagnosis-card__prediction-card'>
                    <div className='diagnosis-card__section-header' id='diagnosis-card__prediction-section-header'>
                        <span className='diagnosis-card__section-icon' id='diagnosis-card__prediction-section-icon'><IconPrediction /></span>
                        <h3>{translate('ABB.Powertrain.Frontend.predictionLabel')}</h3>
                    </div>
                    {diagnosis?.isNeedsToShowRemainingUsefulLife ? renderPredictionWithRUL() : renderPredictionSimple()}
                </div>

                {/* Confirming Diagnosis Card */}
                {diagnosis?.recommendationToConfirmDiagnosis?.text &&
                    <div className='diagnosis-card__card diagnosis-card__recommendation-card' id='diagnosis-card__recommendation-card-confirm'>
                        <div className='diagnosis-card__section-header' id='diagnosis-card__recommendation-section-header-confirm'>
                            <span className='diagnosis-card__section-icon' id='diagnosis-card__recommendation-section-icon-confirm'><IconRecommendation /></span>
                            <h3>{translate('ABB.Powertrain.Frontend.recommendationsLabel')}</h3>
                        </div>
                        <h4 className='diagnosis-card__recommendation-title' id='diagnosis-card__recommendation-title-confirm'>
                            {getTranslateSmartConditionTitle(diagnosis?.recommendationToConfirmDiagnosis)}
                        </h4>
                        {renderRecommendationText(diagnosis?.recommendationToConfirmDiagnosis)}
                    </div>
                }

                {/* Fixing the Problem Card */}
                {diagnosis?.recommendationToFixProblem?.text &&
                    <div className='diagnosis-card__card diagnosis-card__recommendation-card' id='diagnosis-card__recommendation-card-fix'>
                        <div className='diagnosis-card__section-header' id='diagnosis-card__recommendation-section-header-fix'>
                            <span className='diagnosis-card__section-icon' id='diagnosis-card__recommendation-section-icon-fix'><IconRecommendation /></span>
                            <h3>{translate('ABB.Powertrain.Frontend.recommendationsLabel')}</h3>
                        </div>
                        <h4 className='diagnosis-card__recommendation-title' id='diagnosis-card__recommendation-title-fix'>
                            {getTranslateSmartConditionTitle(diagnosis?.recommendationToFixProblem)}
                        </h4>
                        {renderRecommendationText(diagnosis?.recommendationToFixProblem)}
                    </div>
                }
            </div>
        </div>
    );
};

DiagnosisCard.propTypes = {
    diagnosis: PropTypes.shape({
        failureType: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string
        }),
        faultDetectionLevel: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string
        }),
        diagnosis: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string
        }),
        causes: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string,
            titleKey: PropTypes.string,
            title: PropTypes.string
        }),
        prediction: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string,
            titleKey: PropTypes.string,
            title: PropTypes.string
        }),
        recommendationToConfirmDiagnosis: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string,
            titleKey: PropTypes.string,
            title: PropTypes.string
        }),
        recommendationToFixProblem: PropTypes.shape({
            textKey: PropTypes.string,
            text: PropTypes.string,
            titleKey: PropTypes.string,
            title: PropTypes.string
        }),
        isNeedsToShowRemainingUsefulLife: PropTypes.bool,
        remainingUsefulLifeTime: PropTypes.number,
        alertThreshold: PropTypes.string,
        alarmThreshold: PropTypes.string,
    }),
    diagnosisNumber: PropTypes.number
};

export default DiagnosisCard;
